import cn from 'classnames';
import CarouselSelector from 'components/carousel-selector';
import ProductCard, { CARD_TYPES } from 'components/product-card';
import ProductImpressionsTracker from 'components/product-impressions-tracker';
import useMobile from 'hooks/common/use-mobile';
import { CollectionProduct } from 'models/collection/collection';
import { FC, RefObject, useState } from 'react';
import Slider, { Settings } from 'react-slick';
import style from '../../style.module.css';

export interface ProductSliderProps {
  collectionHandle: string;
  collectionTitle: string;
  elementNameTracking?: string[];
  additionalSlide?: React.ReactNode;
  products: CollectionProduct[];
  sliderProps?: Settings;
  sliderRef?: RefObject<Slider>;
  sliderSize?: 'default' | 'medium' | 'large';
  afterChange?: () => void;
  onTileClick?: (product: CollectionProduct) => void;
}

const ProductSlider: FC<ProductSliderProps> = ({
  collectionHandle,
  collectionTitle,
  elementNameTracking,
  additionalSlide,
  products,
  sliderProps,
  sliderRef,
  sliderSize,
  afterChange,
  onTileClick,
}) => {
  const isMobile = useMobile();
  const [currentSlide, setCurrentSlide] = useState(0);

  const totalItems = additionalSlide ? products.length + 1 : products.length;
  const slidesToShow = sliderProps?.slidesToShow ?? 1;
  const pageCount = Math.ceil(totalItems / slidesToShow);

  return (
    <div className={cn(style.slickContainer)}>
      <Slider
        key={collectionHandle} // workaround to set to render the first slide in correct position
        dots={false}
        ref={sliderRef}
        {...sliderProps}
        beforeChange={(_: number, next: number) => setCurrentSlide(next)}
        afterChange={() => afterChange?.()}
      >
        {products.map((product, index: number) => {
          return (
            <ProductImpressionsTracker
              collectionHandle={collectionHandle}
              element={
                (elementNameTracking && elementNameTracking[0]) ?? 'Collection Section Bestseller'
              }
              key={`${product.id}-${product.promoteVariantId}`}
              list={collectionTitle}
              position={index}
              product={product}
              trackingGroupName={collectionHandle}
            >
              <div className={cn('mx-2 h-full', sliderSize === 'large' && 'w-[90%]')}>
                <ProductCard
                  collectionTitle={collectionTitle}
                  position={index}
                  product={product}
                  starSize={sliderSize === 'large' || !isMobile ? 'medium' : 'small'}
                  type={sliderSize === 'large' ? CARD_TYPES.SLIDER_LARGE : CARD_TYPES.SLIDER}
                  onTileClick={onTileClick ? () => onTileClick?.(product) : undefined}
                />
              </div>
            </ProductImpressionsTracker>
          );
        })}
        {additionalSlide && <div className="h-full">{additionalSlide}</div>}
      </Slider>

      {isMobile && pageCount > 1 && (
        <div data-test="product-slider-pagination">
          <CarouselSelector
            className="mt-3"
            slidesLength={pageCount}
            selectedSlideIndex={currentSlide}
            onClick={setCurrentSlide}
          />
        </div>
      )}
    </div>
  );
};

export default ProductSlider;
